// ─────────────────────────────────────────────────────────────
// LeftPaneQueue.js - Queue visualization for sources or risks
// Shows progress, current item, completed items, waiting items
// ─────────────────────────────────────────────────────────────

import React from 'react';
import { 
  Stack, Card, Text, Badge, Group, Title 
} from '@mantine/core';
import { 
  IconCheck, IconClock, IconTarget, IconAlertTriangle 
} from '@tabler/icons-react';

export default function LeftPaneQueue({ 
  items = [], 
  currentItem,
  type = 'sources', // 'sources' or 'risks'
  phase,
  onItemClick
}) {
  
  const getItemStatus = (item) => {
    // DEBUG: Log status calculation
    console.log(`[LeftPaneQueue] Status check for item:`, {
      type,
      itemId: type === 'sources' ? item.name : item.riskId,
      itemStatus: item.status,
      currentItem,
      currentItemId: currentItem ? (type === 'sources' ? currentItem.name : currentItem.riskId) : null
    });
    
    // If item has explicit status, use it (for risks that have been processed)
    if (item.status === 'completed') return 'completed';
    
    if (!currentItem) return 'waiting';
    
    // Check if this item is currently being processed
    const isCurrent = type === 'sources' 
      ? item.name === currentItem.name 
      : item.riskId === currentItem.riskId;
    
    if (isCurrent) return 'current';
    
    // For items without explicit status, use index-based logic
    const currentIndex = items.findIndex(i => 
      type === 'sources' ? i.name === currentItem.name : i.riskId === currentItem.riskId
    );
    const itemIndex = items.findIndex(i => 
      type === 'sources' ? i.name === item.name : i.riskId === item.riskId
    );
    
    console.log(`[LeftPaneQueue] Index calculation:`, {
      currentIndex,
      itemIndex,
      willBeCompleted: itemIndex < currentIndex
    });
    
    if (itemIndex < currentIndex) return 'completed';
    return 'waiting';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <IconCheck size={16} color="#51cf66" />;
      case 'current': return <IconTarget size={16} color="#339af0" />;
      case 'waiting': return <IconClock size={16} color="#adb5bd" />;
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'green';
      case 'current': return 'blue'; 
      case 'waiting': return 'gray';
      default: return 'gray';
    }
  };

  const getQueueTitle = () => {
    if (type === 'sources') return 'Risk Discovery';
    
    // For risks, use phase-aware titles
    if (phase === 'complete') return 'Monitoring & Reporting';
    return 'Risk Evaluation';
  };
  
  const queueTitle = getQueueTitle();

  return (
    <Stack gap="lg">
      {/* Queue Header */}
      <div>
        <Title order={4} c="dark.7" mb="md">
          {queueTitle}
        </Title>
      </div>

      {/* Queue Items */}
      <Stack gap="sm">
        {items.map((item, index) => {
          const status = getItemStatus(item);
          const isClickable = onItemClick && (status === 'completed' || status === 'current');
          
          return (
            <Card
              key={type === 'sources' ? item.name : item.riskId}
              withBorder
              radius="md"
              p="md"
              onClick={isClickable ? () => onItemClick(item) : undefined}
              sx={(theme) => ({
                cursor: isClickable ? 'pointer' : 'default',
                border: status === 'current' 
                  ? `2px solid ${theme.colors.blue[5]}` 
                  : `1px solid ${theme.colors.gray[2]}`,
                backgroundColor: status === 'completed' 
                  ? theme.colors.green[0] 
                  : status === 'current'
                  ? theme.colors.blue[0]
                  : theme.white,
                '&:hover': isClickable ? {
                  boxShadow: theme.shadows.sm
                } : {}
              })}
            >
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <div style={{ flex: 1 }}>
                  <Group gap="xs" mb="xs">
                    {getStatusIcon(status)}
                    <Text 
                      size="sm" 
                      fw={600}
                      c={status === 'waiting' ? 'dimmed' : 'dark.7'}
                      lineClamp={1}
                    >
                      {type === 'sources' ? item.name : item.riskName || item.title || `Risk ${index + 1}`}
                    </Text>
                  </Group>
                  
                  {/* Show saved risk for completed sources */}
                  {type === 'sources' && status === 'completed' && item.savedRisk && (
                    <Text size="xs" c="green.7" lineClamp={1}>
                      → Risk: {item.savedRisk.title}
                    </Text>
                  )}
                  
                  {/* Show risk details for risk processing */}
                  {type === 'risks' && (
                    <Text size="xs" c="dimmed" lineClamp={2}>
                      {item.rationale || item.description || item.scenario || item.category}
                    </Text>
                  )}
                </div>
                
                <Badge 
                  size="xs" 
                  color={getStatusColor(status)}
                  variant={status === 'current' ? 'filled' : 'light'}
                >
                  {status === 'completed' ? 'Done' : 
                   status === 'current' ? 'Active' : 'Waiting'}
                </Badge>
              </Group>
            </Card>
          );
        })}
      </Stack>

      {items.length === 0 && (
        <Card withBorder radius="md" p="lg" bg="gray.0">
          <Group justify="flex-start" gap="xs">
            <IconAlertTriangle size={16} color="#adb5bd" />
            <Text size="sm" c="dimmed">
              No {type} in queue
            </Text>
          </Group>
        </Card>
      )}
    </Stack>
  );
}